import { useContext, useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { context } from "../context/context";
import CircularLoader from "../styled/loaders/CircularLoader";

const AssetsCard = () => {
  const { accounts, currentPrices } = useContext(context);
  const [isLoading, setIsLoading] = useState(true);

  // Priority assets to show when no real assets exist or to fill remaining slots
  const priorityAssets = [
    {
      symbol: "BTC",
      name: "Bitcoin",
      type: "Crypto",
      fallbackValue: 0,
      icon: "./assets/asset/bitcoin.svg",
    },
    {
      symbol: "ETH",
      name: "Ethereum",
      type: "Crypto",
      fallbackValue: 0,
      icon: "./asseticons/ETH.svg",
    },
    {
      symbol: "SOL",
      name: "Solana",
      type: "Crypto",
      fallbackValue: 0,
      icon: "./asseticons/SOL.svg",
    },
    {
      symbol: "AAPL",
      name: "Apple",
      type: "Stocks",
      fallbackValue: 0,
      icon: "./asseticons/AAPL.svg",
    },
    {
      symbol: "GOOGL",
      name: "Alphabet",
      type: "Stocks",
      fallbackValue: 0,
      icon: "./asseticons/GOOGL.svg",
    },
    {
      symbol: "USD",
      name: "United States Dollars",
      type: "Fiat",
      fallbackValue: 0,
      icon: "./asseticons/USD.svg",
    },
  ];

  const assets = useMemo(() => {
    if (
      !accounts ||
      !currentPrices ||
      Object.keys(currentPrices).length === 0
    ) {
      return [];
    }

    const { live } = accounts;
    if (!live) return [];

    const maxAssets = 4;

    // Get real assets first
    const realAssets = [];

    // Get top crypto assets
    if (live.Crypto) {
      Object.entries(live.Crypto)
        .filter(([_, asset]) => asset.value > 0)
        .sort(
          ([_, a], [__, b]) =>
            b.value * (currentPrices[b.asset] || 0) -
            a.value * (currentPrices[a.asset] || 0)
        )
        .forEach(([symbol, asset]) => {
          realAssets.push({
            name: asset.name,
            symbol,
            value: asset.value,
            price: currentPrices[symbol] || 0,
            type: "Crypto",
            isReal: true,
            icon: `./asseticons/${symbol}.svg`,
          });
        });
    }

    // Get top fiat assets
    if (live.Fiat) {
      Object.entries(live.Fiat)
        .filter(([_, asset]) => asset.value > 0)
        .sort(([_, a], [__, b]) => b.value - a.value)
        .forEach(([symbol, asset]) => {
          realAssets.push({
            name: asset.name,
            symbol,
            value: asset.value,
            price: currentPrices[symbol] || 1, // Fiat typically has price of 1
            type: "Fiat",
            isReal: true,
            icon: `./asseticons/${symbol}.svg`,
          });
        });
    }

    // Get top stock assets
    if (live.Stock) {
      Object.entries(live.Stock)
        .filter(([_, asset]) => asset.value > 0)
        .sort(
          ([_, a], [__, b]) =>
            b.value * (currentPrices[b.asset] || 0) -
            a.value * (currentPrices[a.asset] || 0)
        )
        .forEach(([symbol, asset]) => {
          realAssets.push({
            name: asset.name,
            symbol,
            value: asset.value,
            price: currentPrices[symbol] || 0,
            type: "Stocks",
            isReal: true,
            icon: `./asseticons/${symbol}.svg`,
          });
        });
    }

    // If we have real assets, prioritize them and fill remaining slots with fallback assets
    if (realAssets.length > 0) {
      // Prioritize BTC, ETH, AAPL, USD if they exist in real assets
      const prioritySymbols = ["BTC", "ETH", "AAPL", "USD"];
      const prioritized = [];
      const others = [];

      realAssets.forEach((asset) => {
        if (prioritySymbols.includes(asset.symbol)) {
          prioritized.push(asset);
        } else {
          others.push(asset);
        }
      });

      // Sort prioritized assets by priority order
      prioritized.sort((a, b) => {
        const aIndex = prioritySymbols.indexOf(a.symbol);
        const bIndex = prioritySymbols.indexOf(b.symbol);
        return aIndex - bIndex;
      });

      // Sort others by value
      others.sort(
        (a, b) =>
          b.value * (currentPrices[b.symbol] || 0) -
          a.value * (currentPrices[a.symbol] || 0)
      );

      // Combine real assets
      const combinedRealAssets = [...prioritized, ...others];

      // Get symbols of real assets to avoid duplicates
      const realAssetSymbols = new Set(
        combinedRealAssets.map((asset) => asset.symbol)
      );

      // Filter out fallback assets that already exist as real assets
      const availableFallbackAssets = priorityAssets.filter(
        (asset) => !realAssetSymbols.has(asset.symbol)
      );

      // Convert fallback assets to the same format as real assets
      const fallbackAssetsFormatted = availableFallbackAssets.map((asset) => ({
        name: asset.name,
        symbol: asset.symbol,
        value: asset.fallbackValue,
        price: currentPrices[asset.symbol] || 0,
        type: asset.type,
        isReal: false,
        icon: asset.icon,
      }));

      // Combine real assets with fallback assets and limit to maxAssets
      const allAssets = [...combinedRealAssets, ...fallbackAssetsFormatted];
      return allAssets.slice(0, maxAssets);
    }

    // If no real assets, show fallback priority assets
    return priorityAssets.slice(0, maxAssets).map((asset) => ({
      name: asset.name,
      symbol: asset.symbol,
      value: asset.fallbackValue,
      price: currentPrices[asset.symbol] || 0,
      type: asset.type,
      isReal: false,
      icon: asset.icon,
    }));
  }, [accounts, currentPrices]);

  useEffect(() => {
    if (accounts && currentPrices) {
      setIsLoading(false);
    }
  }, [accounts, currentPrices]);

  const { formatter } = useContext(context);

  return (
    <Container>
      <div className="top">Top Assets</div>

      <div className="wrapper">
        {isLoading ? (
          <div
            style={{
              backgroundColor: "transparent",
              padding: "60px",
            }}
          >
            <CircularLoader
              bg="rgba(12, 108, 243, 0.2)"
              size="28"
              color="#0C6CF2"
            />
          </div>
        ) : (
          <div className="assets">
            {assets.length > 0 ? (
              assets.map((asset) => (
                <div className="asset" key={asset.symbol}>
                  <div className="asset_left">
                    <img src={asset.icon} alt="" />
                    <span>
                      <p>{asset.name}</p>
                      <p>{asset.symbol}</p>
                    </span>
                  </div>

                  <span className="asset_right">
                    <p>{asset.value.toFixed(2)}</p>
                    {asset.value ? (
                      <p>{formatter.format(asset.price * asset.value)}</p>
                    ) : (
                      <p> {} </p>
                    )}
                  </span>
                </div>
              ))
            ) : (
              <div
                style={{
                  height: "100%",
                  minHeight: "200px",
                  // backgroundColor: "red",
                  display: "flex",
                  placeContent: "center",
                }}
              >
                <CircularLoader
                  bg="rgba(12, 108, 243, 0.2)"
                  size="28"
                  color="#0C6CF2"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Container>
  );
};

const Container = styled.div`
  grid-area: assets;
  background-color: #151823;
  border-radius: 12px;
  width: 78%;
  padding: 24px 0px;
  background-color: #151823cc;
  border-left: 1px solid #1b1f2d;
  border-bottom-left-radius: 0px;
  border-top-left-radius: 0px;

  @media screen and (max-width: 1540px) {
    display: none;
  }

  @media screen and (max-width: 1250px) {
    display: block;

    width: 100%;
  }

  @media screen and (max-width: 1100px) {
    border-radius: 12px;
  }

  .top {
    color: white;
    font-size: 18px;
    font-weight: 600;
    background-color: #1b1f2d;
    width: 100%;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    padding: 12px 24px;
    background-color: transparent;
  }

  .wrapper {
    padding: 16px;
    display: grid;
    gap: 24px;
  }

  .assets {
    display: grid;
    gap: 16px;
    font-family: "Inter";
    font-feature-settings: "tnum", "lnum", "zero", "ss01";
  }

  .asset {
    display: flex;
    justify-content: space-between;
    padding: 4px;
  }

  .asset_left {
    display: flex;
    gap: 8px;
  }

  .asset_left img {
    height: 32px;
    width: 32px;
  }

  .asset_left span {
    display: grid;
    gap: 4px;
  }

  .asset_left span p:nth-child(1) {
    color: white;
    font-size: 16px;
    font-weight: 500;
  }

  .asset_left span p:nth-child(2) {
    color: #bac2de;
    font-size: 14px;
    font-weight: 600;
  }

  .asset_right {
    display: grid;
    gap: 4px;
    // backgroundColor: red;
    text-align: right;
  }

  .asset_right p:nth-child(1) {
    color: white;
    font-size: 16px;
    font-weight: 500;
  }

  .asset_right p:nth-child(2) {
    color: #bac2de;
    font-size: 14px;
    font-weight: 600;
  }
`;

export default AssetsCard;
